import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Box, 
  Paper, 
  Typography, 
  TextField, 
  Button, 
  Grid, 
  Checkbox, 
  FormControlLabel, 
  FormGroup, 
  CircularProgress,
  Alert,
  Divider,
  Switch,
  Card,
  CardContent,
  Avatar,
  Fade,
  Chip
} from '@mui/material';
import { 
  Edit as EditIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon
} from '@mui/icons-material';
import { useForm, Controller, Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import PageHeader from '../components/common/PageHeader';
import { UserService } from '../services/user.service';
import { User, Role } from '../types/auth.types';
import { useSelector } from 'react-redux';
import { selectUser } from '../store/slices/authSlice';

const schema = yup.object({
  username: yup.string().required('Имя пользователя обязательно'),
  email: yup.string()
    .email('Введите корректный email')
    .required('Email обязателен'),
  fullName: yup.string().required('Полное имя обязательно'),
  password: yup.string()
    .min(8, 'Пароль должен содержать минимум 8 символов')
    .notRequired(),
  roleIds: yup.array().min(1, 'Выберите хотя бы одну роль'),
  isActive: yup.boolean(),
}).required();

interface UserFormData {
  username: string;
  email: string;
  fullName: string;
  password?: string;
  roleIds: string[];
  isActive: boolean;
}

export default function UserEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const currentUser = useSelector(selectUser);
  
  const [user, setUser] = useState<User | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  
  const { control, handleSubmit, formState: { errors }, setValue } = useForm<UserFormData>({
    resolver: yupResolver(schema) as Resolver<UserFormData>,
    defaultValues: {
      username: '',
      email: '',
      fullName: '',
      password: '',
      roleIds: [],
      isActive: true,
    }
  });
  
  useEffect(() => {
    setMounted(true);
    if (id) {
      loadData(id);
    }
  }, [id]);
  
  const loadData = async (userId: string) => {
    try {
      setLoading(true);
      
      // Загружаем пользователя и роли параллельно
      const [userData, rolesData] = await Promise.all([
        UserService.getUserById(userId),
        UserService.getAllRoles(),
      ]);
      
      setUser(userData);
      setRoles(rolesData);
      
      setValue('username', userData.username);
      setValue('email', userData.email);
      setValue('fullName', userData.fullName);
      setValue('roleIds', userData.roles.map(role => role.id));
      setValue('isActive', userData.isActive === undefined ? true : userData.isActive);
      
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: UserFormData) => {
    if (!id) return;
    
    if (id === currentUser?.id && !data.isActive) {
      setError('Вы не можете деактивировать свою учетную запись');
      return;
    }
    
    try {
      setSubmitting(true);
      setError(null);
      
      const userData = { ...data };
      if (!userData.password) {
        delete userData.password;
      }
      
      await UserService.updateUser(id, userData);
      
      setSuccess('Пользователь успешно обновлен');
      setTimeout(() => {
        navigate('/users');
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка обновления пользователя');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleCancel = () => {
    navigate('/users');
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} sx={{ color: '#667eea' }} />
      </Box>
    );
  }
  
  if (!user) {
    return (
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="error">
          Пользователь не найден
        </Typography>
        <Button variant="contained" onClick={() => navigate('/users')} sx={{ mt: 2 }}>
          Вернуться к списку пользователей
        </Button>
      </Box>
    );
  }
  
  return (
    <Box>
      <Fade in={mounted} timeout={400}>
        <Box
          sx={{
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            borderRadius: 3,
            p: 4,
            mb: 4,
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '-50%',
              right: '-20%',
              width: '400px',
              height: '400px',
              background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
            }
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                <EditIcon sx={{ fontSize: 28 }} />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Редактирование пользователя
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  {user.fullName}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Fade>
      
      {error && (
        <Fade in timeout={400}>
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        </Fade>
      )}
      
      {success && (
        <Fade in timeout={400}>
          <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
            {success}
          </Alert>
        </Fade>
      )}
      
      <form onSubmit={handleSubmit(onSubmit)}>
        <Fade in={mounted} timeout={600}>
          <Card sx={{ mb: 3, borderRadius: 3 }}>
            <Box sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', p: 2.5, color: 'white' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <PersonIcon />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Личная информация
                </Typography>
              </Box>
            </Box>
            
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="username"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Имя пользователя"
                        fullWidth
                        required
                        error={!!errors.username}
                        helperText={errors.username?.message}
                        disabled={submitting || id === currentUser?.id}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': { borderColor: '#4facfe' },
                            '&.Mui-focused fieldset': { borderColor: '#4facfe' }
                          }
                        }}
                      />
                    )}
                  />
                </Grid>
                
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="fullName"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Полное имя"
                        fullWidth
                        required
                        error={!!errors.fullName}
                        helperText={errors.fullName?.message}
                        disabled={submitting}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': { borderColor: '#4facfe' },
                            '&.Mui-focused fieldset': { borderColor: '#4facfe' }
                          }
                        }}
                      />
                    )}
                  />
                </Grid>
                
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Email"
                        type="email"
                        fullWidth
                        required
                        error={!!errors.email}
                        helperText={errors.email?.message}
                        disabled={submitting}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': { borderColor: '#4facfe' },
                            '&.Mui-focused fieldset': { borderColor: '#4facfe' }
                          }
                        }}
                      />
                    )}
                  />
                </Grid>
                
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="password"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Новый пароль"
                        type="password"
                        fullWidth
                        placeholder="Оставьте пустым, чтобы не изменять"
                        error={!!errors.password}
                        helperText={errors.password?.message}
                        disabled={submitting}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': { borderColor: '#4facfe' },
                            '&.Mui-focused fieldset': { borderColor: '#4facfe' }
                          }
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Fade>
        
        <Fade in={mounted} timeout={800}>
          <Card sx={{ mb: 3, borderRadius: 3 }}>
            <Box sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', p: 2.5, color: 'white' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <ToggleOnIcon />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Статус аккаунта
                </Typography>
              </Box>
            </Box>
            
            <CardContent sx={{ p: 3 }}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      border: '2px solid',
                      borderColor: field.value ? '#4caf50' : '#f44336',
                      bgcolor: field.value ? 'rgba(76, 175, 80, 0.05)' : 'rgba(244, 67, 54, 0.05)',
                      transition: 'all 0.3s'
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={field.onChange}
                          disabled={submitting || id === currentUser?.id}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#4caf50',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: '#4caf50',
                            },
                          }}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar sx={{ 
                            bgcolor: field.value ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)', 
                            color: field.value ? '#4caf50' : '#f44336',
                            width: 40,
                            height: 40
                          }}>
                            {field.value ? <ToggleOnIcon /> : <ToggleOffIcon />}
                          </Avatar>
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              {field.value ? 'Пользователь активен' : 'Пользователь заблокирован'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {field.value ? 'Может входить в систему' : 'Вход в систему запрещен'}
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                  </Box>
                )}
              />
            </CardContent>
          </Card>
        </Fade>
        
        <Fade in={mounted} timeout={1000}>
          <Card sx={{ mb: 3, borderRadius: 3 }}>
            <Box sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', p: 2.5, color: 'white' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <SecurityIcon />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Роли пользователя
                </Typography>
              </Box>
            </Box>
            
            <CardContent sx={{ p: 3 }}>
              <Controller
                name="roleIds"
                control={control}
                render={({ field }) => (
                  <Box>
                    <FormGroup>
                      {roles.map((role) => {
                        const isCurrentUserAdmin = id === currentUser?.id && role.name === 'ADMIN';
                        return (
                          <Box
                            key={role.id}
                            sx={{
                              p: 2,
                              mb: 1,
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: field.value.includes(role.id) ? '#f5576c' : 'divider',
                              bgcolor: field.value.includes(role.id) ? 'rgba(245, 87, 108, 0.05)' : 'transparent',
                              opacity: isCurrentUserAdmin ? 0.6 : 1,
                              transition: 'all 0.2s',
                              '&:hover': {
                                borderColor: isCurrentUserAdmin ? 'divider' : '#f5576c',
                                bgcolor: isCurrentUserAdmin ? 'transparent' : 'rgba(245, 87, 108, 0.05)',
                              }
                            }}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={field.value.includes(role.id)}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    const newRoleIds = checked
                                      ? [...field.value, role.id]
                                      : field.value.filter(roleId => roleId !== role.id);
                                    
                                    if (isCurrentUserAdmin && !checked) {
                                      return;
                                    }
                                    
                                    field.onChange(newRoleIds);
                                  }}
                                  disabled={submitting || isCurrentUserAdmin}
                                  sx={{
                                    color: '#f5576c',
                                    '&.Mui-checked': { color: '#f5576c' }
                                  }}
                                />
                              }
                              label={
                                <Box>
                                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                    {role.name}
                                    {isCurrentUserAdmin && (
                                      <Chip 
                                        label="Нельзя убрать" 
                                        size="small" 
                                        sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                                      />
                                    )}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {role.description || 'Без описания'}
                                  </Typography>
                                </Box>
                              }
                            />
                          </Box>
                        );
                      })}
                    </FormGroup>
                    {errors.roleIds && (
                      <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                        {errors.roleIds.message}
                      </Typography>
                    )}
                  </Box>
                )}
              />
            </CardContent>
          </Card>
        </Fade>
        
        <Fade in={mounted} timeout={1200}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={submitting}
              startIcon={<CancelIcon />}
              sx={{
                borderColor: '#f5576c',
                color: '#f5576c',
                '&:hover': {
                  borderColor: '#f5576c',
                  bgcolor: 'rgba(245, 87, 108, 0.08)'
                }
              }}
            >
              Отмена
            </Button>
            
            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={20} /> : <SaveIcon />}
              sx={{
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #e082ea 0%, #e4465b 100%)'
                }
              }}
            >
              {submitting ? 'Сохранение...' : 'Сохранить изменения'}
            </Button>
          </Box>
        </Fade>
      </form>
    </Box>
  );
}